import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Truck, CheckCircle, Edit, AlertCircle, History, Clock, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getCurrentUser } from '../utils/sessionManager';
import ActivityLogButton from './ActivityLogButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Table Header Component
const SortableHeader = ({ field, label, sortBy, sortOrder, onSort }) => (
  <TableHead 
    className="cursor-pointer hover:bg-slate-100 select-none"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {label}
      {sortBy === field && (
        sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
      )}
    </div>
  </TableHead>
);

// Pagination Component
const PaginationControls = ({ pagination, onPageChange }) => {
  const { page, totalPages, totalCount, pageSize } = pagination;
  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalCount);
  
  return (
    <div className="flex items-center justify-between mt-4 px-2">
      <div className="text-sm text-slate-600">
        Showing {startRecord} to {endRecord} of {totalCount} shipments
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={page === 1}
        >
          First
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-sm px-2">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
        >
          Last
        </Button>
      </div>
    </div>
  );
};

const VendorShipmentManagement = ({ userPermissions }) => {
  // Debug: Log permissions
  console.log('VendorShipmentManagement - userPermissions:', userPermissions);
  console.log('VendorShipmentManagement - shipment permissions:', userPermissions?.shipment);
  console.log('VendorShipmentManagement - granular permissions:', {
    shipment_vendor: userPermissions?.shipment_vendor,
    shipment_driver: userPermissions?.shipment_driver,
    shipment_store: userPermissions?.shipment_store
  });
  
  // Check if current user is admin_user (for past date shipment permission)
  const currentUser = getCurrentUser() || {};
  const isAdminUser = currentUser?.username === 'admin_user';
  
  // Use GRANULAR shipment permissions for role-based access
  // Vendor Portal: Create & Ready for Pickup
  const canAccessVendorPortal = 
    userPermissions?.shipment_vendor?.create || 
    userPermissions?.shipment_vendor?.edit || 
    userPermissions?.shipment_vendor?.view || false;
  
  const canCreateVendorShipment = userPermissions?.shipment_vendor?.create || false;
  const canEditVendorShipment = userPermissions?.shipment_vendor?.edit || false;
  
  // Driver Portal: In-Transit Management
  const canAccessDriverPortal = 
    userPermissions?.shipment_driver?.create || 
    userPermissions?.shipment_driver?.edit || 
    userPermissions?.shipment_driver?.view || false;
  
  const canEditDriverShipment = userPermissions?.shipment_driver?.edit || false;
  
  // Store Portal: Receive & GRN
  const canAccessStorePortal = 
    userPermissions?.shipment_store?.create || 
    userPermissions?.shipment_store?.edit || 
    userPermissions?.shipment_store?.view || false;
  
  const canCreateStoreGRN = userPermissions?.shipment_store?.create || false;
  const canEditStoreShipment = userPermissions?.shipment_store?.edit || false;
  
  console.log('VendorShipmentManagement - Granular Permission checks:', {
    canAccessVendorPortal,
    canCreateVendorShipment,
    canAccessDriverPortal,
    canAccessStorePortal
  });
  
  // User needs at least one portal access
  const hasShipmentAccess = canAccessVendorPortal || canAccessDriverPortal || canAccessStorePortal;
  
  const [activeRole, setActiveRole] = useState('vendor');
  const [shipments, setShipments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [pos, setPOs] = useState([]);
  const [allPOs, setAllPOs] = useState([]);  // All POs for displaying PO numbers
  const [warehouses, setWarehouses] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('created_ts');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Create shipment dialog (Vendor)
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newShipment, setNewShipment] = useState({
    po_id: '',
    vendor_id: '',
    item_id: '',
    qty: '',
    warehouse_id: '',
    ready_date: new Date().toISOString().split('T')[0]
  });
  const [shipmentNumber, setShipmentNumber] = useState('');
  const [originalPoQty, setOriginalPoQty] = useState(null);
  const [createRemarks, setCreateRemarks] = useState('');
  
  // Edit quantity dialog (All roles)
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [editedQty, setEditedQty] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  
  // History dialog
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyShipment, setHistoryShipment] = useState(null);

  const [userVendorId, setUserVendorId] = useState(null);

  // Set initial active role based on granular permissions
  useEffect(() => {
    if (canAccessVendorPortal) {
      setActiveRole('vendor');
    } else if (canAccessDriverPortal) {
      setActiveRole('driver');
    } else if (canAccessStorePortal) {
      setActiveRole('store');
    }
  }, [canAccessVendorPortal, canAccessDriverPortal, canAccessStorePortal]);

  // Get logged-in user's vendor ID and initialize once
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const user = getCurrentUser();
        if (user) {
          if (user.vendor_id) {
            setUserVendorId(user.vendor_id);
          } else {
            // User doesn't have vendor_id, safe to fetch all data
            fetchData();
            fetchShipments();
          }
        } else {
          // No user data, fetch all
          fetchData();
          fetchShipments();
        }
      } catch (error) {
        console.error('Error getting user vendor ID:', error);
        // On error, fetch all data
        fetchData();
        fetchShipments();
      }
    };
    
    initializeUser();
  }, []);

  useEffect(() => {
    // Only fetch when userVendorId is set (not null)
    if (userVendorId) {
      fetchData();
      fetchShipments();
    }
  }, [userVendorId]);

  const fetchData = async () => {
    try {
      // Filter POs by vendor if user is a vendor
      const posUrl = userVendorId 
        ? `${API}/procurement/pos?vendor_id=${userVendorId}`
        : `${API}/procurement/pos`;
      
      const posWithoutShipmentUrl = userVendorId
        ? `${API}/procurement/pos-without-shipment?vendor_id=${userVendorId}`
        : `${API}/procurement/pos-without-shipment`;
      
      const [vendorRes, itemRes, poResWithoutShipment, allPOsRes, warehouseRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/items`),
        axios.get(posWithoutShipmentUrl),
        axios.get(posUrl),  // Fetch POs for display
        axios.get(`${API}/warehouses`)
      ]);
      setVendors(vendorRes.data);
      setItems(itemRes.data);
      setPOs(poResWithoutShipment.data);  // For create dialog dropdown
      // Handle response - now returns array directly
      const allPOData = Array.isArray(allPOsRes.data) ? allPOsRes.data : (allPOsRes.data.data || []);
      setAllPOs(allPOData);  // For displaying PO numbers
      setWarehouses(warehouseRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchShipments = async (page = pagination.page, pageSize = pagination.pageSize) => {
    try {
      // Build URL with pagination and sorting
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (userVendorId) {
        params.append('vendor_id', userVendorId);
      }
      
      const response = await axios.get(`${API}/vendor-shipment?${params.toString()}`);
      
      // Handle new paginated response format
      if (response.data.data) {
        setShipments(response.data.data);
        setPagination({
          page: response.data.pagination.page,
          pageSize: response.data.pagination.page_size,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages
        });
      } else {
        // Fallback for old format
        setShipments(response.data);
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  // Re-fetch when sort changes
  useEffect(() => {
    if (userVendorId !== null) {
      fetchShipments(1, pagination.pageSize);
    }
  }, [sortBy, sortOrder]);

  const handlePageChange = (newPage) => {
    fetchShipments(newPage, pagination.pageSize);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const generateShipmentNumber = async () => {
    try {
      const response = await axios.post(`${API}/vendor-shipment/generate-number`);
      setShipmentNumber(response.data.shipment_number);
    } catch (error) {
      console.error('Error generating shipment number:', error);
      toast.error('Failed to generate shipment number');
    }
  };

  const handlePOSelect = async (poId) => {
    try {
      const response = await axios.get(`${API}/vendor-shipment/po-items/${poId}`);
      const poData = response.data;
      
      // Defensive numeric coercion — protects the field from ever appearing blank
      const rawQty = poData?.qty;
      const numericQty = (rawQty === null || rawQty === undefined || rawQty === '') ? 0 : Number(rawQty);
      const safeQty = Number.isFinite(numericQty) ? numericQty : 0;
      
      setOriginalPoQty(safeQty);
      setCreateRemarks('');
      setNewShipment({
        ...newShipment,
        po_id: poId,
        vendor_id: poData.vendor_id,
        item_id: poData.item_id,
        qty: String(safeQty),
        order_date: poData.order_date || ''
      });
      
      toast.success('PO details loaded');
    } catch (error) {
      console.error('Error loading PO:', error);
      toast.error('Failed to load PO details');
    }
  };

  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleCreateShipment = async () => {
    console.log('=== CREATE SHIPMENT CLICKED ===');
    console.log('Form data:', newShipment);
    console.log('Shipment number:', shipmentNumber);
    
    if (!newShipment.po_id || !newShipment.qty || !newShipment.warehouse_id) {
      console.error('Validation failed:', {
        po_id: newShipment.po_id,
        qty: newShipment.qty,
        warehouse_id: newShipment.warehouse_id
      });
      toast.error('Please fill all required fields');
      return;
    }

    // If quantity differs from the original PO qty, remarks are mandatory
    const enteredQty = parseFloat(newShipment.qty);
    const qtyChanged = originalPoQty !== null && enteredQty !== originalPoQty;
    if (qtyChanged && !createRemarks.trim()) {
      toast.error('Remarks are mandatory when you change the quantity from the MRO quantity');
      return;
    }

    // Validate PO order date is not in the past
    const selectedPO = allPOs.find(po => po.id === newShipment.po_id);
    if (selectedPO && selectedPO.order_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const orderDate = new Date(selectedPO.order_date);
      orderDate.setHours(0, 0, 0, 0);
      
      if (orderDate < today) {
        toast.error('Cannot create shipment for PO with past order date');
        return;
      }
    }

    try {
      const shipmentData = {
        id: generateUUID(),
        shipment_number: shipmentNumber,
        po_id: newShipment.po_id,
        vendor_id: newShipment.vendor_id,
        item_id: newShipment.item_id,
        qty: parseFloat(newShipment.qty),
        warehouse_id: newShipment.warehouse_id,
        ready_date: newShipment.ready_date,
        shipment_status: 'READY_FOR_PICKUP',
        created_ts: new Date().toISOString(),
        // Optional remarks captured at creation (recorded in shipment history)
        creation_notes: createRemarks.trim() || null,
        original_po_qty: originalPoQty,
        qty_changed_at_creation: qtyChanged
      };
      
      console.log('Sending shipment data:', shipmentData);

      // Pass user_id as query param for admin users to allow past date shipments
      const url = isAdminUser 
        ? `${API}/vendor-shipment/create?user_id=${currentUser.id}`
        : `${API}/vendor-shipment/create`;
      
      await axios.post(url, shipmentData);
      toast.success('Shipment created successfully!');
      
      setShowCreateDialog(false);
      setNewShipment({
        po_id: '',
        vendor_id: '',
        item_id: '',
        qty: '',
        warehouse_id: '',
        ready_date: new Date().toISOString().split('T')[0]
      });
      setCreateRemarks('');
      setOriginalPoQty(null);
      
      fetchShipments();
      fetchData();
    } catch (error) {
      console.error('=== ERROR CREATING SHIPMENT ===');
      console.error('Error object:', error);
      console.error('Response:', error.response);
      console.error('Response data:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create shipment');
    }
  };

  const handleEditQuantity = (shipment) => {
    setEditingShipment(shipment);
    setEditedQty(shipment.qty);
    setEditRemarks('');
    setShowEditDialog(true);
  };

  const handleSaveQuantity = async () => {
    const newQty = parseFloat(editedQty);
    const currentQty = parseFloat(editingShipment?.qty);
    if (!newQty || newQty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    // If quantity has changed, remarks are mandatory
    if (newQty !== currentQty && !editRemarks.trim()) {
      toast.error('Remarks are mandatory when the quantity is changed');
      return;
    }
    try {
      setEditSaving(true);
      await axios.put(`${API}/vendor-shipment/${editingShipment.id}/update-qty`, {
        qty: newQty,
        notes: editRemarks.trim()
      });
      toast.success('Quantity updated successfully!');
      setShowEditDialog(false);
      setEditRemarks('');
      fetchShipments();
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast.error(error.response?.data?.detail || 'Failed to update quantity');
    } finally {
      setEditSaving(false);
    }
  };

  const handleMarkInTransit = async (shipmentId) => {
    try {
      const pickup_date = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/vendor-shipment/${shipmentId}/mark-in-transit`, {
        pickup_date: pickup_date
      });
      toast.success('Shipment marked as in transit');
      fetchShipments();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleMarkReceived = async (shipmentId) => {
    try {
      const received_date = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/vendor-shipment/${shipmentId}/mark-received`, {
        received_date: received_date,
        create_grn: true
      });
      toast.success('Shipment received and GRN created!');
      fetchShipments();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as received');
    }
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || 'N/A';
  const getItemName = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : 'N/A';
  };
  const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || 'N/A';
  const getPONumber = (id) => allPOs.find(p => p.id === id)?.po_number || 'N/A';
  
  // Get the original "Ready for Pickup" quantity from history
  const getOriginalReadyQty = (shipment) => {
    if (shipment.history && shipment.history.length > 0) {
      // Find the first READY_FOR_PICKUP entry (initial quantity)
      const firstReadyEntry = shipment.history.find(h => h.status === 'READY_FOR_PICKUP' && h.qty);
      if (firstReadyEntry) {
        return firstReadyEntry.qty;
      }
    }
    // Fallback to current qty
    return shipment.qty;
  };

  // Get quantity for each stage from history
  const getStageQuantity = (shipment, stage) => {
    if (shipment.history && shipment.history.length > 0) {
      const stageEntry = shipment.history.find(h => h.status === stage && h.qty);
      if (stageEntry) {
        return stageEntry.qty;
      }
    }
    
    // If no history, show current qty only for current stage
    if (shipment.shipment_status === stage) {
      return shipment.qty;
    }
    
    return '-';
  };

  const getStatusBadge = (shipment_status) => {
    const statusMap = {
      'READY_FOR_PICKUP': { label: 'Ready for Pickup', variant: 'default' },
      'IN_TRANSIT': { label: 'In Transit', variant: 'secondary' },
      'RECEIVED': { label: 'Received', variant: 'success' }
    };
    const config = statusMap[shipment_status] || { label: shipment_status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleViewHistory = (shipment) => {
    setHistoryShipment(shipment);
    setShowHistoryDialog(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'READY_FOR_PICKUP':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'IN_TRANSIT':
        return <Truck className="w-4 h-4 text-orange-600" />;
      case 'RECEIVED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'QTY_UPDATE':
        return <Edit className="w-4 h-4 text-purple-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  // Filter shipments by role - show all relevant shipments for each role
  // Vendor: Show all shipments they created (READY_FOR_PICKUP, IN_TRANSIT, RECEIVED)
  const vendorShipments = shipments; // Show all shipments to vendor
  
  // Driver: Show shipments available for pickup AND currently in transit
  const driverShipments = shipments.filter(s => 
    s.shipment_status === 'READY_FOR_PICKUP' || s.shipment_status === 'IN_TRANSIT'
  );
  
  // Store: Show in-transit (to receive) AND already received shipments
  const storeShipments = shipments.filter(s => 
    s.shipment_status === 'IN_TRANSIT' || s.shipment_status === 'RECEIVED'
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Shipment Management - Role Based</h2>
        <p className="text-slate-600">
          Three separate workflows for Vendor, Driver, and Store Person with quantity editing at each step.
        </p>
      </div>

      {/* Role Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Your Role</CardTitle>
          <CardDescription>Choose your role to access relevant shipment operations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {canAccessVendorPortal && (
              <Button
                variant={activeRole === 'vendor' ? 'default' : 'outline'}
                onClick={() => setActiveRole('vendor')}
                className="flex-1"
              >
                <Package className="w-4 h-4 mr-2" />
                Vendor (Create)
              </Button>
            )}
            
            {canAccessDriverPortal && (
              <Button
                variant={activeRole === 'driver' ? 'default' : 'outline'}
                onClick={() => setActiveRole('driver')}
                className="flex-1"
              >
                <Truck className="w-4 h-4 mr-2" />
                Driver (In Transit)
              </Button>
            )}
            
            {canAccessStorePortal && (
              <Button
                variant={activeRole === 'store' ? 'default' : 'outline'}
                onClick={() => setActiveRole('store')}
                className="flex-1"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Store (Receive)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vendor Section */}
      {activeRole === 'vendor' && canAccessVendorPortal && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Vendor Portal - Create & Ready for Pickup
                </CardTitle>
                <CardDescription>
                  Create new shipments and mark them ready for pickup
                </CardDescription>
              </div>
              {canCreateVendorShipment && (
                <Button onClick={() => {
                    generateShipmentNumber();
                    setShowCreateDialog(true);
                  }}>
                    Create Shipment
                  </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-900">Vendor Role</p>
                  <p className="text-sm text-orange-700">
                    You can create shipments from POs and mark them as ready for pickup. You can also edit quantities before pickup.
                  </p>
                </div>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="shipment_number" label="Shipment #" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <TableHead>PO</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Item</TableHead>
                  <SortableHeader field="order_date" label="Order Date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <TableHead className="text-right">Ready for Pickup</TableHead>
                  <TableHead className="text-right">In Transit</TableHead>
                  <TableHead className="text-right">Store Received</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <SortableHeader field="ready_date" label="Ready Date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader field="shipment_status" label="Status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendorShipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-slate-500">
                      No shipments ready for pickup. Create a new shipment above.
                    </TableCell>
                  </TableRow>
                ) : (
                  vendorShipments.map((shipment) => (
                    <TableRow key={shipment.id}>
                      <TableCell className="font-medium">{shipment.shipment_number}</TableCell>
                      <TableCell className="font-medium">{getPONumber(shipment.po_id)}</TableCell>
                      <TableCell className="text-sm">{getVendorName(shipment.vendor_id)}</TableCell>
                      <TableCell>{getItemName(shipment.item_id)}</TableCell>
                      <TableCell>{shipment.order_date || '-'}</TableCell>
                      <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'READY_FOR_PICKUP')}</TableCell>
                      <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'IN_TRANSIT')}</TableCell>
                      <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'RECEIVED')}</TableCell>
                      <TableCell>{getWarehouseName(shipment.warehouse_id)}</TableCell>
                      <TableCell>{shipment.ready_date}</TableCell>
                      <TableCell>{getStatusBadge(shipment.shipment_status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewHistory(shipment)}
                            title="View History"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <ActivityLogButton recordId={shipment.id} recordLabel={shipment.shipment_number} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            {/* Pagination Controls */}
            {pagination.totalCount > 0 && (
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver Section */}
      {activeRole === 'driver' && canAccessDriverPortal && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Driver Portal - In Transit Management
            </CardTitle>
            <CardDescription>
              Mark shipments as in transit and manage transportation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Driver Role</p>
                  <p className="text-sm text-blue-700">
                    Mark shipments as in transit when you pick them up. You can edit quantities if needed during transport.
                  </p>
                </div>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="text-right">Ready for Pickup</TableHead>
                  <TableHead className="text-right">In Transit</TableHead>
                  <TableHead className="text-right">Store Received</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Show all relevant shipments for driver */}
                {driverShipments.map((shipment) => (
                  <TableRow key={shipment.id} className={shipment.shipment_status === 'IN_TRANSIT' ? 'bg-blue-50' : ''}>
                    <TableCell className="font-medium">{shipment.shipment_number}</TableCell>
                    <TableCell>{getVendorName(shipment.vendor_id)}</TableCell>
                    <TableCell>{getItemName(shipment.item_id)}</TableCell>
                    <TableCell>{shipment.order_date || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'READY_FOR_PICKUP')}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'IN_TRANSIT')}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'RECEIVED')}</TableCell>
                    <TableCell>{getWarehouseName(shipment.warehouse_id)}</TableCell>
                    <TableCell>{shipment.pickup_date || shipment.ready_date}</TableCell>
                    <TableCell>{getStatusBadge(shipment.shipment_status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {shipment.shipment_status === 'READY_FOR_PICKUP' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditQuantity(shipment)}
                            title="Edit Quantity"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewHistory(shipment)}
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <ActivityLogButton recordId={shipment.id} recordLabel={shipment.shipment_number} />
                        {canEditDriverShipment && shipment.shipment_status === 'READY_FOR_PICKUP' && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkInTransit(shipment.id)}
                          >
                            Mark In Transit
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {driverShipments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500">
                      No shipments available for pickup or in transit.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            {/* Pagination Controls */}
            {pagination.totalCount > 0 && (
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Store Person Section */}
      {activeRole === 'store' && canAccessStorePortal && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Store Person Portal - Receive & GRN
            </CardTitle>
            <CardDescription>
              Mark shipments as received and create GRN entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Store Person Role</p>
                  <p className="text-sm text-green-700">
                    Receive in-transit shipments and create GRN. You can edit quantities upon receipt if actual quantity differs.
                  </p>
                </div>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment #</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="text-right">Ready for Pickup</TableHead>
                  <TableHead className="text-right">In Transit</TableHead>
                  <TableHead className="text-right">Store Received</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Transit Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Show all relevant shipments for store person */}
                {storeShipments.map((shipment) => (
                  <TableRow key={shipment.id} className={shipment.shipment_status === 'RECEIVED' ? 'bg-green-50' : ''}>
                    <TableCell className="font-medium">{shipment.shipment_number}</TableCell>
                    <TableCell>{getVendorName(shipment.vendor_id)}</TableCell>
                    <TableCell>{getItemName(shipment.item_id)}</TableCell>
                    <TableCell>{shipment.order_date || '-'}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'READY_FOR_PICKUP')}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'IN_TRANSIT')}</TableCell>
                    <TableCell className="text-right font-semibold">{getStageQuantity(shipment, 'RECEIVED')}</TableCell>
                    <TableCell>{getWarehouseName(shipment.warehouse_id)}</TableCell>
                    <TableCell>{shipment.received_date || shipment.pickup_date}</TableCell>
                    <TableCell>{getStatusBadge(shipment.shipment_status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {shipment.shipment_status === 'IN_TRANSIT' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditQuantity(shipment)}
                            title="Edit Quantity"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewHistory(shipment)}
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <ActivityLogButton recordId={shipment.id} recordLabel={shipment.shipment_number} />
                        {canCreateStoreGRN && shipment.shipment_status === 'IN_TRANSIT' && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkReceived(shipment.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Mark Received
                          </Button>
                        )}
                        {shipment.shipment_status === 'RECEIVED' && (
                          <Badge variant="success">Complete</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {storeShipments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-slate-500">
                      No shipments in transit or received.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            {/* Pagination Controls */}
            {pagination.totalCount > 0 && (
              <PaginationControls pagination={pagination} onPageChange={handlePageChange} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Shipment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Shipment</DialogTitle>
            <DialogDescription>
              Create a new vendor shipment from a purchase order
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Shipment Number (Auto-generated)</Label>
              <Input value={shipmentNumber} disabled />
            </div>

            <div>
              <Label>Milk Run Order *</Label>
              <select
                className="w-full border rounded-md p-2"
                value={newShipment.po_id}
                onChange={(e) => handlePOSelect(e.target.value)}
              >
                <option value="">Select PO</option>
                {pos.filter(po => {
                  // Admin users can see all POs including past dates
                  if (isAdminUser) return true;
                  // Non-admin users: Only show POs with order_date >= today
                  if (!po.order_date) return true; // Show POs without date
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const orderDate = new Date(po.order_date);
                  orderDate.setHours(0, 0, 0, 0);
                  return orderDate >= today;
                }).map(po => (
                  <option key={po.id} value={po.id}>
                    {po.po_number} - {getItemName(po.item_id)} - Qty: {po.qty}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                {isAdminUser 
                  ? "Admin: Showing all POs including past dates"
                  : "Only showing POs with order date today or in the future"
                }
              </p>
            </div>

            {/* Row 1: Vendor and Item */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor</Label>
                <Input value={getVendorName(newShipment.vendor_id)} disabled />
              </div>
              <div>
                <Label>Item</Label>
                <Input value={getItemName(newShipment.item_id)} disabled />
              </div>
            </div>

            {/* Row 2: MRO Order Date and Ready Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>MRO Order Date</Label>
                <Input value={newShipment.order_date || '-'} disabled />
              </div>
              <div>
                <Label>Ready Date *</Label>
                <Input
                  type="date"
                  value={newShipment.ready_date}
                  onChange={(e) => setNewShipment({ ...newShipment, ready_date: e.target.value })}
                  min={isAdminUser ? undefined : new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Row 3: Quantity and Warehouse */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity * (Editable)</Label>
                <Input
                  type="number"
                  value={newShipment.qty}
                  onChange={(e) => setNewShipment({ ...newShipment, qty: e.target.value })}
                  placeholder="Enter quantity"
                  data-testid="create-shipment-qty-input"
                />
                {originalPoQty !== null && parseFloat(newShipment.qty) !== originalPoQty && (
                  <p className="text-xs text-amber-600 mt-1">
                    Quantity differs from MRO qty ({originalPoQty}). Remarks required below.
                  </p>
                )}
              </div>
              <div>
                <Label>Warehouse *</Label>
                <select
                  className="w-full border rounded-md p-2 h-10"
                  value={newShipment.warehouse_id}
                  onChange={(e) => setNewShipment({ ...newShipment, warehouse_id: e.target.value })}
                >
                  <option value="">Select warehouse</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 4: Remarks (mandatory if qty differs from MRO qty) */}
            <div>
              <Label>
                Remarks {originalPoQty !== null && parseFloat(newShipment.qty) !== originalPoQty && (
                  <span className="text-red-500">*</span>
                )}
              </Label>
              <Textarea
                value={createRemarks}
                onChange={(e) => setCreateRemarks(e.target.value)}
                placeholder="Reason for quantity change (mandatory if changed from MRO qty)"
                rows={2}
                data-testid="create-shipment-remarks-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Remarks will be recorded in the shipment history for audit.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateShipment}>
              Create Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quantity Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Quantity</DialogTitle>
            <DialogDescription>
              Update the shipment quantity. Remarks are mandatory when quantity is changed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Shipment Number</Label>
              <Input value={editingShipment?.shipment_number || ''} disabled />
            </div>

            <div>
              <Label>Current Quantity</Label>
              <Input value={editingShipment?.qty || ''} disabled />
            </div>

            <div>
              <Label>New Quantity *</Label>
              <Input
                type="number"
                value={editedQty}
                onChange={(e) => setEditedQty(e.target.value)}
                placeholder="Enter new quantity"
                data-testid="edit-qty-new-input"
              />
            </div>

            <div>
              <Label>
                Remarks {parseFloat(editedQty) !== parseFloat(editingShipment?.qty) && (
                  <span className="text-red-500">*</span>
                )}
              </Label>
              <Textarea
                value={editRemarks}
                onChange={(e) => setEditRemarks(e.target.value)}
                placeholder="Reason for quantity change (mandatory if qty changed)"
                rows={3}
                data-testid="edit-qty-remarks-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Remarks will be recorded in the shipment history for audit.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveQuantity} disabled={editSaving} data-testid="edit-qty-save-btn">
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipment History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Shipment History - {historyShipment?.shipment_number}
            </DialogTitle>
            <DialogDescription>
              Track quantity movement through different stages
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Shipment Summary */}
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-slate-600">Vendor:</span>
                    <span className="ml-2">{getVendorName(historyShipment?.vendor_id)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Item:</span>
                    <span className="ml-2">{getItemName(historyShipment?.item_id)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Current Status:</span>
                    <span className="ml-2">{getStatusBadge(historyShipment?.shipment_status)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-600">Current Quantity:</span>
                    <span className="ml-2 font-bold text-lg">{historyShipment?.qty}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* History Timeline */}
            <div>
              <h3 className="font-semibold mb-3 text-slate-700">Activity Timeline</h3>
              {historyShipment?.history && historyShipment.history.length > 0 ? (
                <div className="space-y-3">
                  {historyShipment.history.map((entry, index) => (
                    <div key={index} className="flex gap-4 items-start pb-3 border-b last:border-b-0">
                      <div className="mt-1">
                        {getStatusIcon(entry.status)}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-slate-900">
                              {entry.status === 'READY_FOR_PICKUP' && 'Ready for Pickup'}
                              {entry.status === 'IN_TRANSIT' && 'In Transit'}
                              {entry.status === 'RECEIVED' && 'Received in Store'}
                              {entry.status === 'QTY_UPDATE' && 'Quantity Updated'}
                            </p>
                            <p className="text-sm text-slate-600 mt-1">
                              Quantity: <span className="font-semibold">{entry.qty}</span>
                            </p>
                            {entry.notes && (
                              <div className="mt-1 text-sm">
                                <span className="font-semibold text-slate-700">Remarks:</span>{' '}
                                <span className="text-slate-600 italic">{entry.notes}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : entry.date}</p>
                            <p className="text-xs">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No history available for this shipment yet.</p>
                  <p className="text-sm mt-1">History will be recorded as the shipment moves through different stages.</p>
                </div>
              )}
            </div>

            {/* Stage Summary */}
            {historyShipment?.history && historyShipment.history.length > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-blue-900">Stage Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="flex justify-center mb-1">
                        <Package className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-xs text-blue-700 mb-1">Ready for Pickup</p>
                      <p className="font-bold text-blue-900">
                        {historyShipment.history.find(h => h.status === 'READY_FOR_PICKUP')?.qty || '-'}
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-center mb-1">
                        <Truck className="w-5 h-5 text-orange-600" />
                      </div>
                      <p className="text-xs text-orange-700 mb-1">In Transit</p>
                      <p className="font-bold text-orange-900">
                        {historyShipment.history.find(h => h.status === 'IN_TRANSIT')?.qty || '-'}
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-center mb-1">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <p className="text-xs text-green-700 mb-1">Received</p>
                      <p className="font-bold text-green-900">
                        {historyShipment.history.find(h => h.status === 'RECEIVED')?.qty || '-'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowHistoryDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorShipmentManagement;
